import { useState } from "react";
import { CircularProgress, Dialog } from "@mui/material";
import Button from "./Button";
import ShowToast from "./alerts/all_toasts";
import BaseService from "../helpers/baseServices";
import { RouteConstants } from "../helpers/RouteConstants";
import { StorageBox } from "../helpers/stroage";

interface Props {
  isOpen: boolean;
  onClose: () => void;
  onCreated: () => void;
}

const digitsOnly = (value: string) => value.replace(/\D/g, "").slice(0, 4);

const WalletSetupDialog = ({ isOpen, onClose, onCreated }: Props) => {
  const [pin, setPin] = useState("");
  const [confirmPin, setConfirmPin] = useState("");
  const [error, setError] = useState("");
  const [isBusy, setIsBusy] = useState(false);

  const handleSubmit = async () => {
    if (pin.length !== 4) {
      setError("Your PIN must be exactly 4 digits.");
      return;
    }
    if (pin !== confirmPin) {
      setError("PINs don't match.");
      return;
    }
    setError("");
    setIsBusy(true);
    try {
      const response = await BaseService.post_api(RouteConstants.WALLET_ROUTE, {
        pin,
      });
      const walletId = response?.data?.payload?.id;
      if (walletId) StorageBox.saveWalletId(walletId);
      setPin("");
      setConfirmPin("");
      onCreated();
      ShowToast.success_toast("Your wallet is ready");
    } catch (e: any) {
      // Shown inline — toasts render behind an open dialog.
      setError(e?.response?.data?.message || e?.message);
    } finally {
      setIsBusy(false);
    }
  };

  return (
    <Dialog open={isOpen} maxWidth="sm" fullWidth onClose={onClose}>
      <div className="p-5">
        <div className="header-text-3">Set up your wallet</div>
        <div className="body-text-2 py-2">
          Choose a 4-digit PIN. You'll use it to pay and withdraw from your
          wallet.
        </div>

        <div className="flex flex-col mt-4">
          <small className="mb-2">Wallet PIN</small>
          <input
            className="input"
            type="password"
            inputMode="numeric"
            value={pin}
            onChange={(e) => setPin(digitsOnly(e.target.value))}
            placeholder="4 digits"
          />
        </div>
        <div className="flex flex-col mt-4">
          <small className="mb-2">Confirm PIN</small>
          <input
            className="input"
            type="password"
            inputMode="numeric"
            value={confirmPin}
            onChange={(e) => setConfirmPin(digitsOnly(e.target.value))}
            placeholder="Re-enter your PIN"
          />
        </div>
        {error && <div className="text-red-500 mt-2">{error}</div>}

        <div className="mt-6 flex items-center gap-3">
          <Button variant="secondary" width="w-full" onClick={onClose}>
            Not now
          </Button>
          <Button width="w-full" disabled={isBusy} onClick={handleSubmit}>
            {isBusy ? (
              <CircularProgress size={24} sx={{ color: "white" }} />
            ) : (
              <span>Create wallet</span>
            )}
          </Button>
        </div>
      </div>
    </Dialog>
  );
};

export default WalletSetupDialog;
